import { Text } from '#app/components/Primitives/Text.js';
import {
  type ArrowPosition,
  type TooltipCompoundProps,
} from './OnboardingTooltip.js';
import { css, styled } from 'styled-components';

export const TooltipWrapper = styled.div<{
  $width: TooltipCompoundProps['width'];
}>`
  ${({ $width }) => css`
    display: inline;
    width: ${$width};
  `}
`;

const getArrowPosition = (position: ArrowPosition) => {
  switch (position) {
    case 'bottom-left':
      return css`
        bottom: -16px;
        left: 32px;
        background: #303030;
      `;
    case 'bottom-right':
      return css`
        bottom: -16px;
        right: 16px;
        background: #1a1a1a;
      `;
    case 'left-bottom':
      return css`
        bottom: 16px;
        left: 1px;
        background: #2f2f2f;
      `;
    case 'left-center':
      return css`
        top: 47.5%;
        left: 1px;
        background: #232323;
      `;
    case 'left-top':
      return css`
        top: 32px;
        left: 1px;
        background: #171717;
      `;
    case 'right-bottom':
      return css`
        bottom: 16px;
        right: -16px;
        background: #141414;
      `;
    case 'right-center':
      return css`
        top: 47.5%;
        right: -16px;
        background: #141414;
      `;
    case 'right-top':
      return css`
        top: 32px;
        right: -16px;
        background: #141414;
      `;
    case 'top-center':
      return css`
        right: 40%;
        top: 1px;
        background: #141414;
      `;
    case 'top-left':
      return css`
        left: 32px;
        top: 1px;
        background: #141414;
      `;
    case 'top-right':
      return css`
        right: 16px;
        top: 1px;
        background: #141414;
      `;
    case 'bottom-center':
    default:
      return css`
        bottom: -16px;
        right: 40%;
        background: #262626;
      `;
  }
};

export const Arrow = styled.div<{ position: ArrowPosition }>`
  ${({ position }) => css`
    &::before {
      ${getArrowPosition(position)}
      transform: rotate(45deg) translateX(-50%);
      position: absolute;
      z-index: -1;
      display: block;
      content: '';
      width: 24px;
      height: 24px;
      border-radius: 2px;
    }
  `}
`;

export const TooltipReferenceContainer = styled.div<{
  $hideOutlineOnFocus?: boolean;
}>`
  ${({ $hideOutlineOnFocus, theme }) => css`
    cursor: pointer;

    &:focus {
      outline: 1px solid
        ${$hideOutlineOnFocus ? 'transparent' : theme.colorsV2.gray60};
    }
  `}
`;

export const TooltipParagraph = styled(Text).attrs({
  textStyle: 'bodySmall',
})`
  ${({ theme }) => css`
    color: ${theme.colorsV2.white};
    margin-bottom: 16px;
  `}
`;

export const TooltipSubtitle = styled(Text).attrs({
  as: 'span',
  textStyle: 'subtitleSmall',
})`
  ${({ theme }) => css`
    color: ${theme.colorsV2.white};
    margin-right: 4px;
  `}
`;

export const TooltipContainer = styled.div<{
  $mode?: 'dark' | 'light';
  $width?: number | string;
}>`
  ${({ $mode = 'dark', $width = '218px', theme }) => css`
    position: relative;
    width: ${typeof $width === 'string' ? $width : `${$width}px`};
    background: ${$mode === 'dark'
      ? css`linear-gradient(
        220.9deg,
        ${theme.colorsV2.black} 51.15%,
        ${theme.colorsV2.gray90} 95.6%
      )`
      : css`
          ${theme.colorsV2.white}
        `};
    color: ${$mode === 'dark' ? theme.colorsV2.white : theme.colorsV2.black};
    border-radius: 14px;
    padding: 16px;
    ${$mode === 'dark'
      ? null
      : css`
          box-shadow:
            -5px 7px 25px 0px rgba(83, 193, 222, 0.14),
            5px -7px 26px 0px rgba(143, 111, 219, 0.14);
        `}

    & > :first-child::before {
      /* arrow */
      z-index: 1;
      background-color: ${$mode === 'dark'
        ? theme.colorsV2.black
        : theme.colorsV2.white};
    }

    ${Text} {
      color: ${$mode === 'dark' ? theme.colorsV2.white : theme.colorsV2.black};
    }
  `}
`;

export const Button = styled.button`
  align-self: flex-end;
`;

export const TooltipActions = styled.div`
  display: flex;
  align-items: center;
  margin-top: 16px;
  justify-content: space-between;
`;
